/**
 * The flow's IPO — Input, Process, Output in words, written before the steps
 * (Ram, 2026-10-29: "We teach C like this — IPO, Algorithm, Flowchart and
 * program"). Optional; stored on main's flow as `ipo: { input, process, output }`.
 * Pure: no React, no store.
 */

const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim();

/** The flow's IPO as stored, each part trimmed to one line ('' when not written). */
export function ipoOf(flow) {
  const i = (flow && flow.ipo) || {};
  return { input: clean(i.input), process: clean(i.process), output: clean(i.output) };
}

// "a whole number" — the words the Algorithm tab's first line uses. Loaded when
// called: codeNextSession reaches the code generator, which imports this file.
const typeText = (t) => {
  try {
    // eslint-disable-next-line global-require
    return require('./codeNextSession').typeTextWords(t);
  } catch (e) {
    return String(t || 'a value');
  }
};

/**
 * A function's IPO (Ram chose A, 2026-10-29): Input and Output come from its
 * Contract — the parameters and what it gives back — so they cannot disagree
 * with it; only Process is the student's words.
 *   { input: 'no (a whole number)', process: 'multiply no by itself',
 *     output: 'gives back a whole number', inputNames: 'no', outputShort: 'a whole number' }
 */
export function functionIpo(flow) {
  const ps = ((flow && flow.parameters) || []).filter((p) => p && p.name);
  const ret = typeText((flow && flow.returnType) || 'void');
  return {
    input: ps.length ? ps.map((p) => `${p.name} (${typeText(p.type)})`).join(', ') : 'nothing',
    process: ipoOf(flow).process,
    output: ret === 'nothing' ? 'gives back nothing' : `gives back ${ret}`,
    inputNames: ps.length ? ps.map((p) => p.name).join(', ') : 'nothing',
    outputShort: ret,
  };
}

// A function's flow (not main). The code generator says so itself (asFunction).
const isFn = (flow, asFunction) => (asFunction === undefined ? !!flow && flow.isEntry === false : !!asFunction);

/** The IPO as shown: main's as written; a function's from its Contract and its Process. */
export const ipoFor = (flow, asFunction) => (isFn(flow, asFunction) ? functionIpo(flow) : ipoOf(flow));

/** Whether there is an IPO to show: for main any part written; for a function, its Process. */
export const hasIpo = (flow, asFunction) => {
  if (isFn(flow, asFunction)) return !!ipoOf(flow).process;
  const i = ipoOf(flow);
  return !!(i.input || i.process || i.output);
};

/** The Start card's quiet line: "In: price, quantity → Out: total cost" (only the parts written). */
export function ipoCardLine(flow) {
  if (isFn(flow)) {
    if (!hasIpo(flow)) return '';
    const f = functionIpo(flow);
    return `In: ${f.inputNames} → Out: ${f.outputShort}`;
  }
  const i = ipoOf(flow);
  const bits = [];
  if (i.input) bits.push(`In: ${i.input}`);
  if (i.output) bits.push(`Out: ${i.output}`);
  if (!bits.length && i.process) bits.push(i.process);
  return bits.join(' → ');
}

/**
 * The IPO as a C comment above main — or above a function, once its Process is
 * written (Ram, 2026-10-29) — only the parts written:
 *   /* Input:   price, quantity
 *      Process: multiply price by quantity
 *      Output:  total cost *\/
 * A "*\/" the student typed would end the comment early, so it is split.
 */
export function ipoComment(flow, asFunction) {
  if (!hasIpo(flow, asFunction)) return '';
  const i = ipoFor(flow, asFunction);
  const rows = [['Input:  ', i.input], ['Process:', i.process], ['Output: ', i.output]]
    .filter(([, v]) => v)
    .map(([k, v]) => `${k} ${v.replace(/\*\//g, '* /')}`);
  if (!rows.length) return '';
  return `/* ${rows.join('\n   ')} */\n`;
}
